import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function SelectMenu({ value, options, placeholder, onChange, className = '', compact = false, ...rest }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && wrapperRef.current.contains(event.target)) {
        return;
      }
      if (menuRef.current && menuRef.current.contains(event.target)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const card = wrapperRef.current?.closest('.card-glass');
    if (!card) return;
    if (open) {
      card.classList.add('select-parent-open');
    } else {
      card.classList.remove('select-parent-open');
    }
    return () => {
      card.classList.remove('select-parent-open');
    };
  }, [open]);

  useEffect(() => {
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const maxMenuHeight = 320;
      let top = rect.bottom + 10;
      let maxHeight = Math.min(maxMenuHeight, viewportHeight - top - 16);

      if (maxHeight < 180) {
        top = Math.max(16, rect.top - maxMenuHeight - 10);
        maxHeight = Math.min(maxMenuHeight, rect.top - 16);
      }

      setMenuStyle({
        top: `${top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: `${Math.max(160, maxHeight)}px`
      });
    };

    if (open) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [open]);

  const activeOption = options.find((option) => option.value === value);

  return (
    <div
      className={`custom-select ${compact ? 'compact' : ''} ${open ? 'open' : ''} ${className}`}
      ref={wrapperRef}
      {...rest}
    >
      <button
        type="button"
        className="select-trigger input-glass"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        ref={triggerRef}
      >
        <span>{activeOption?.label || placeholder}</span>
        <span className={`select-icon ${open ? 'open' : ''}`}>▾</span>
      </button>

      {open && createPortal(
        <div className="select-menu-portal" style={menuStyle} ref={menuRef}>
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              className={`select-option ${value === option.value ? 'active' : ''}`}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span className="option-title">{option.label}</span>
              {option.description && <span className="option-sub">{option.description}</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default SelectMenu;
